/**
 * "You are signed in as someone else" — the visible half of impersonation
 * (PLAN-OPS Phase 4).
 *
 * The plan asks for two things from impersonation and only one of them is the
 * server's: the session has to work, and it has to be **visibly marked**. This
 * component is the second half, and it is written to be impossible to miss or to
 * get rid of:
 *
 * - It is the first child of the app shell, above the mobile top bar and the
 *   sidebar, so it displaces the whole application downward rather than floating
 *   over a corner of it. An operator cannot scroll it away.
 * - There is no dismiss control. The banner is a statement of fact about the
 *   session, and a fact that can be hidden is one somebody will hide and then
 *   forget — which is exactly the failure mode ("I thought I was on my own
 *   account") the marking exists to prevent.
 * - The only thing it offers is the way out.
 *
 * ## Where the flag comes from, and why it could not come from anywhere else
 *
 * `session.impersonatedBy` is `session.impersonated_by` out of D1, by way of
 * `/api/me`. Nothing the browser knows could produce it: an impersonated session
 * is an ordinary session cookie for the target user, indistinguishable from a
 * real sign-in from inside the tab. So the server says so, on the one request the
 * client already makes to ask who it is.
 *
 * ## Stopping is a swap, not a sign-out
 *
 * better-auth deletes the impersonated session and restores the admin's own from
 * the signed `admin_session` cookie it stashed at the start, so the tab comes
 * back as the operator with their own workspace. `session.refresh()` is what
 * re-reads that — the same call the settings page's sign-out makes, and the same
 * mechanism the store re-keys its IndexedDB record on.
 *
 * The refusal case is real rather than defensive: the impersonation session lasts
 * an hour and can outlive the admin session it came from, and then there is
 * nothing to go back to. The banner says so and offers a sign-out, which is the
 * only honest move left.
 */

import { UserRoundCogIcon } from 'lucide-react';
import { useState } from 'react';
import { signOut, stopImpersonating } from '@/auth/client';
import { useSession } from '@/auth/session-context';
import { Button } from '@/components/ui/button';
import { userContent } from '@/lib/posthog';

export function ImpersonationBanner() {
  const session = useSession();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // The ordinary case, by an enormous margin: no banner, no wrapper, nothing in
  // the layout at all. Every session that was not started by an operator lands
  // here.
  if (session.impersonatedBy === null || session.user === null) return null;

  // A tint plus `text-destructive`, rather than a solid destructive fill with its
  // own foreground token: the banner is permanent while the session lasts, and a
  // full-width block of alarm red above every screen in the app is the kind of
  // thing an operator learns to stop seeing.
  return (
    <div
      role="status"
      data-slot="impersonation-banner"
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-destructive/40 bg-destructive/10 px-4 py-2 text-destructive"
    >
      <p className="flex min-w-0 items-center gap-2 text-sm">
        <UserRoundCogIcon aria-hidden="true" className="size-4 shrink-0" />
        <span className="min-w-0">
          You are signed in as{' '}
          <span {...userContent} className="font-medium">
            {session.user.email}
          </span>
          . Everything you do here happens to their account.
        </span>
      </p>
      <span className="flex items-center gap-3">
        {failure !== null && <span className="text-xs">{failure}</span>}
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setFailure(null);
            const result = await stopImpersonating();
            if (!result.ok) {
              // Not a toast: a toast disappears, and this is the one control on
              // a banner that cannot. The fallback is offered in the same breath.
              setFailure(result.message);
              setBusy(false);
              return;
            }
            await session.refresh();
            setBusy(false);
          }}
        >
          {busy ? 'Working…' : 'Stop impersonating'}
        </Button>
        {failure !== null && (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await signOut();
              await session.refresh();
              setBusy(false);
            }}
          >
            Sign out
          </Button>
        )}
      </span>
    </div>
  );
}
